import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  Bookmark,
  Heart,
  MessageCircle,
  Repeat2,
} from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { formatName, resolveImg, timeAgo } from '@/lib/helpers'
import Avatar from '@/components/ui/Avatar'


export default function Saved() {
  const { user: currentUser, loading: authLoading } = useAuth()
  const [posts, setPosts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return
    if (!currentUser) { setLoading(false); return }
    api.users.getMyBookmarks()
      .then((data) => setPosts(data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false))
  }, [authLoading, currentUser])

  if (authLoading || loading) {
    return (
      <div className="max-w-[1024px] mx-auto px-4 py-20 text-center text-gray-400">
        Ачаалж байна...
      </div>
    )
  }

  return (
    <div className="max-w-[1024px] mx-auto px-4 py-8">
      <h1 className="text-xl font-bold text-gray-900 mb-6">Хадгалсан нийтлэлүүд</h1>

      {posts.length === 0 ? (
        <div className="text-center py-16">
          <Bookmark className="size-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400">Одоогоор хадгалсан нийтлэл алга</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4 max-w-[640px]">
          {posts.map((post) => (
            <SavedPostCard
              key={post._id}
              post={post}
              currentUserId={currentUser?._id}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function SavedPostCard({
  post,
  currentUserId,
}: {
  post: any
  currentUserId?: string
}) {
  const [liked, setLiked] = useState(
    Array.isArray(post.likes) && currentUserId
      ? post.likes.some((id: any) => (id?._id || id)?.toString() === currentUserId)
      : false
  )
  const [likeCount, setLikeCount] = useState(
    Array.isArray(post.likes) ? post.likes.length : 0
  )
  const [repostCount, setRepostCount] = useState(
    Array.isArray(post.reposts) ? post.reposts.length : 0
  )
  const commentCount = Array.isArray(post.comments) ? post.comments.length : 0

  const handleLike = async () => {
    try {
      const res = await api.posts.toggleLike(post._id)
      setLiked(res.liked)
      setLikeCount(res.likes)
    } catch {}
  }

  const handleRepost = async () => {
    try {
      const res = await api.posts.toggleRepost(post._id)
      setRepostCount((c: number) => (res.reposted ? c + 1 : c - 1))
    } catch {}
  }

  const author = post.userId || {}
  const authorName = formatName(author.fullName) || 'Хэрэглэгч'
  const authorAvatar = resolveImg(author.avatar)
  const image = resolveImg(post.imageUrl)

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <Link to={`/profile/${author._id || 'me'}`}>
          <Avatar src={authorAvatar} alt={authorName} size="sm" />
        </Link>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-gray-900">{authorName}</span>
          <p className="text-xs text-gray-400">{timeAgo(post.createdAt)}</p>
        </div>
      </div>

      <p className="text-sm text-gray-700 leading-relaxed mb-3 whitespace-pre-line">
        {post.content}
      </p>

      {image && (
        <div className="mb-3">
          <img src={image} alt="" className="w-full h-64 object-cover rounded-xl" />
        </div>
      )}

      <div className="flex items-center gap-1 pt-3 border-t border-gray-100">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition cursor-pointer ${
            liked ? 'text-red-500' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
          }`}
        >
          <Heart className={`size-4 ${liked ? 'fill-current' : ''}`} />
          {likeCount}
        </button>
        <span className="flex items-center gap-1.5 text-gray-400 text-sm px-3 py-1.5">
          <MessageCircle className="size-4" />
          {commentCount}
        </span>
        <button
          onClick={handleRepost}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-green-500 hover:bg-green-50 px-3 py-1.5 rounded-lg transition cursor-pointer"
        >
          <Repeat2 className="size-4" />
          {repostCount}
        </button>
      </div>
    </div>
  )
}
